import { useRef, useEffect } from 'react';
import type { BaseShader } from '../base-shader';

interface ShaderContext {
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  uniformLocations: Record<string, WebGLUniformLocation | null>;
  startTime: number;
  animationId: number | null;
}

/**
 * Hook for managing WebGL shader previews with seamless uniform updates
 */
export function useShaderPreview<T extends BaseShader>(
  model: T,
  getUniformNames: () => string[],
  updateUniforms: (
    gl: WebGLRenderingContext,
    locations: Record<string, WebGLUniformLocation | null>,
    model: T
  ) => void
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<ShaderContext | null>(null);

  // Initialize WebGL context and shaders (regenerate when model changes)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    const vertexShaderSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;
    const fragmentShaderSource = model.generate();

    // Create and compile shaders
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    if (!vertexShader) return;
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
      console.error('Vertex shader error:', gl.getShaderInfoLog(vertexShader));
      return;
    }

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    if (!fragmentShader) return;
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
      console.error(
        'Fragment shader error:',
        gl.getShaderInfoLog(fragmentShader)
      );
      return;
    }

    // Create and link program
    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    // Setup geometry
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const positions = [-1, -1, 1, -1, -1, 1, 1, 1];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

    const positionAttributeLocation = gl.getAttribLocation(
      program,
      'a_position'
    );
    gl.enableVertexAttribArray(positionAttributeLocation);
    gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const uniformNames = [
      'u_time',
      'u_resolution',
      'u_reference_resolution',
      'u_rotation',
      ...getUniformNames(),
    ];
    const uniformLocations: Record<string, WebGLUniformLocation | null> = {};

    for (const name of uniformNames) {
      uniformLocations[name] = gl.getUniformLocation(program, name);
    }

    const startTime = Date.now();

    // Store context
    contextRef.current = {
      gl,
      program,
      uniformLocations,
      startTime,
      animationId: null,
    };

    // Set initial viewport
    gl.viewport(0, 0, canvas.width, canvas.height);

    // Start render loop
    const render = () => {
      if (!contextRef.current) return;

      // Update viewport to match canvas size
      gl.viewport(0, 0, canvas.width, canvas.height);

      const elapsedTime = (Date.now() - contextRef.current.startTime) / 1000;
      gl.uniform1f(uniformLocations.u_time, elapsedTime);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      contextRef.current.animationId = requestAnimationFrame(render);
    };
    render();

    return () => {
      if (contextRef.current?.animationId) {
        cancelAnimationFrame(contextRef.current.animationId);
      }
      contextRef.current = null;
    };
  }, [model.name]); // Regenerate when shader type changes

  // Update uniforms when model changes (without restarting animation)
  useEffect(() => {
    const context = contextRef.current;
    const canvas = canvasRef.current;
    if (!context || !canvas) return;

    const { gl, uniformLocations } = context;

    // Update viewport when canvas size changes
    gl.viewport(0, 0, canvas.width, canvas.height);

    // Update common uniforms
    gl.uniform2f(uniformLocations.u_resolution, model.width, model.height);

    // Only set reference resolution for texture shaders
    if (uniformLocations.u_reference_resolution) {
      gl.uniform2f(
        uniformLocations.u_reference_resolution,
        model.referenceWidth,
        model.referenceHeight
      );
    }

    // Set rotation uniform
    gl.uniform1f(uniformLocations.u_rotation, model.rotation || 0);

    // Update shader-specific uniforms
    updateUniforms(gl, uniformLocations, model);
  }, [model, updateUniforms]);

  return canvasRef;
}
